import React, { FC, ReactElement } from 'react';
import Link from '@mui/material/Link';
import { Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { clsx } from 'clsx';
// Component
import Text from '../text';
import menuContent, { MenuInfos } from './menuContent';
import Space, { Type } from '../layout/space';
import Separator from '../layout/separator';
// Resources
import { getUser } from '../../localStorageAPI';
import { rps, stringsRes } from '../../resources/strings';
import { isExternalUrl } from '../../utils/redirect';

interface DrawerMenuResponsiveProps {
    onClick?: (location: string) => void;
    noAdmin?: boolean;
}

const DrawerMenuResponsiveV2: FC<DrawerMenuResponsiveProps> = (props): ReactElement => {
    const { onClick, noAdmin = false } = props;

    const menuEntries: MenuInfos[] = menuContent.menuBuilder({});
    const menuAdminEntries: MenuInfos[] = menuContent.menuAdminBuilder({});
    const classes = useStyles();

    const renderMenu = (data: MenuInfos[]): JSX.Element[] => {
        return data.map((menu: MenuInfos): JSX.Element => {
            const { pathname } = window.location;
            const isSelected = pathname === menu.href || pathname.startsWith(menu.href);
            return (
                <React.Fragment key={menu.href}>
                    <Link
                        {...{
                            to: onClick ? undefined : menu.href,
                            target: !onClick && isExternalUrl(menu.href) ? '_blank' : '',
                            rel: 'noopener noreferrer',
                            component: onClick ? undefined : RouterLink,
                            onClick: onClick ? () => onClick(menu.href) : undefined,
                            color: 'inherit',
                            style: { textDecoration: 'none', display: 'flex', alignItems: 'center' },
                        }}
                        underline="hover"
                    >
                        {menu.icon}
                        <Space value={2} type={Type.horizontal} />
                        <Text.ShortMediumText16
                            text={menu.label}
                            className={clsx(classes.greyLabel, classes.paddingText, isSelected && classes.selected)}
                        />
                    </Link>
                    <Space value={2} />
                </React.Fragment>
            );
        });
    };

    return (
        <div className={classes.drawerMenuRoot}>
            <Space value={4} />
            <Text.H25
                text={rps(stringsRes.booking.headerHelloResponsive, { name: getUser()?.firstname || '' })}
                className={classes.paddingText}
            />
            <Space value={4} />
            {renderMenu(menuEntries)}
            {!noAdmin && (
                <>
                    <Separator height={1} />
                    <Space value={4} />
                    <Text.H25 text={stringsRes.roles.principal} className={classes.paddingText} />
                    <Space value={4} />
                    {renderMenu(menuAdminEntries)}
                </>
            )}
            <Separator height={1} />
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawerMenuRoot: {
            overflow: 'auto',
            padding: '0px 30px',
            color: theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.light,
        },
        greyLabel: {
            color: theme.palette.colors.text.contrast,
        },
        paddingText: {
            color: theme.palette.secondary.contrastText,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        selected: {
            color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.secondary.light,
        },
    }),
);

export default DrawerMenuResponsiveV2;
